import { BROKERS } from "@/data/brokers";

const TIER1 = ["FCA", "ASIC", "FINMA", "MAS", "BaFin", "KNF", "DFSA", "FMA"];
const TIER2 = ["CySEC", "FSCA", "CMA"];

export const entityFor = (broker, country) => {
  if (!country) return broker.entities[0];
  return broker.entities.find((e) => e.regions.includes(country)) || null;
};

const depositFloor = { "Under $100": 0, "$100 – $500": 100, "$500 – $2,000": 500, "$2,000 – $10,000": 2000, "Over $10,000": 10000 };

// Each fit returns 0..1. Scores are rules-based and never consider commercial terms.
const fits = {
  bonus: (b, ent) => (b.depositBonus && (!ent || ent.bonus) ? (b.depositBonus.startsWith("100%") ? 1 : 0.8) : 0),
  swapFree: (b) => (!b.swapFree ? 0 : /Unconditional|default/.test(b.swapFreeNote) ? 1 : /30 days|14-day/.test(b.swapFreeNote) ? 0.55 : 0.75),
  rawSpreads: (b) => Math.max(0, 1 - (b.eurUsdSpread * 10 + b.commission) / 20),
  fastFunding: (b) => {
    const t = b.withdrawalTime.toLowerCase();
    const speed = /instant|< 30|under 1/.test(t) ? 1 : /same day|under 2/.test(t) ? 0.85 : 0.6;
    return speed * 0.75 + Math.min(b.funding.length, 6) / 6 * 0.25;
  },
  reliability: (b, ent) => {
    const top = ent?.regulator || b.entities[0].regulator;
    const reg = TIER1.includes(top) ? 1 : TIER2.includes(top) ? 0.7 : 0.4;
    const age = Math.min((2026 - b.founded) / 15, 1);
    return reg * 0.6 + (b.trust / 10) * 0.3 + age * 0.1;
  },
};

export function scoreBroker(broker, prefs) {
  const { criteria = {}, country, platform, deposit } = prefs;
  const ent = entityFor(broker, country);
  if (country && !ent) return { score: 0, eligible: false, breakdown: [], entity: null };

  const entries = Object.entries(criteria);
  let weight = 0, sum = 0;
  const breakdown = entries.map(([id, level]) => {
    const w = level === "essential" ? 2 : 1;
    const fit = fits[id](broker, ent);
    weight += w; sum += fit * w;
    return { id, level, fit };
  });

  let score = entries.length ? (sum / weight) * 100 : 70 + broker.trust * 2.5;
  const notes = [];
  if (platform && platform !== "No preference") {
    if (broker.platforms.includes(platform)) { score += 4; notes.push(`${platform} supported`); }
    else { score -= 18; notes.push(`${platform} not offered`); }
  }
  if (deposit) {
    const floor = depositFloor[deposit];
    if (broker.minDeposit > floor + 100) { score -= 15; notes.push(`Min deposit $${broker.minDeposit} above your range`); }
  }
  const essentialMiss = breakdown.some((b) => b.level === "essential" && b.fit < 0.4);
  if (essentialMiss) score *= 0.7;

  return { score: Math.round(Math.max(8, Math.min(98, score))), eligible: true, breakdown, entity: ent, notes };
}

export function rankBrokers(prefs, limit, brokers = BROKERS) {
  const ranked = brokers.map((b) => ({ broker: b, ...scoreBroker(b, prefs) }))
    .filter((r) => r.eligible)
    .sort((a, b) => b.score - a.score);
  return limit ? ranked.slice(0, limit) : ranked;
}
